/**
 * References de champ KeePass (`{REF:...}`), resolues a l'import d'une base KDBX / XML (`kdbx-import.ts`) comme
 * d'un export CSV KeePass (`csv-import.ts`). Purement local, sans E/S ; aucune valeur journalisee.
 */

/**
 * Reference de champ KeePass : `{REF:<voulu>@<cherche dans>:<texte>}`. Un mot de passe partage entre deux entrees
 * s'ecrit `{REF:P@I:<UUID en hexadecimal>}` ; KeePass, KeePassXC et KeeWeb la resolvent a l'affichage, mais l'export
 * garde le texte brut. Importee telle quelle, l'entree aurait pour mot de passe ce texte : elle n'ouvrirait plus rien.
 */
const REFERENCE_PATTERN = /\{REF:([A-Z])@([A-Z]):([^}]+)\}/giu;

/** Champs que l'on peut vouloir (`voulu`) ; `I` (l'UUID lui-meme) ne sert a rien hors de KeePass : laisse tel quel. */
const REFERENCE_WANTED: Readonly<Record<string, string>> = { T: 'Title', U: 'UserName', P: 'Password', A: 'URL', N: 'Notes' };

/** Champs natifs, au sens de la recherche `O` (« autres champs ») des references. */
const REFERENCE_NATIVE_KEYS: ReadonlySet<string> = new Set(['Title', 'UserName', 'Password', 'URL', 'Notes']);

/** Reference dans une reference : KeePassXC s'arrete a 10 niveaux. */
const MAX_REFERENCE_DEPTH = 10;

export interface ReferenceTarget {
  /** UUID KeePass en hexadecimal majuscule (forme des references `@I:`). */
  readonly uuid: string;
  readonly strings: ReadonlyMap<string, string>;
  /** Entree reprise au coffre (hors corbeille KeePass) : seule une entree reprise peut etre liee. */
  readonly live: boolean;
}

/** Toutes les entrees du document (corbeille comprise, comme KeePass), historiques exclus. */
export interface ReferenceIndex {
  readonly entries: readonly ReferenceTarget[];
  readonly byUuid: ReadonlyMap<string, ReferenceTarget>;
}

/**
 * Entree designee. `@I` : l'UUID exact. Ailleurs (`T`, `U`, `P`, `A`, `N`, `O`) KeePass prend la PREMIERE entree dont
 * le champ contient le texte, sans casse -- un ordre de parcours qui n'est pas le meme d'un logiciel a l'autre. Ici
 * la reference ne se resout que si UNE seule entree correspond : jamais le mot de passe d'une autre entree au hasard.
 */
function findReferenceTarget(index: ReferenceIndex, searchIn: string, text: string): ReferenceTarget | null {
  if (searchIn === 'I') return index.byUuid.get(text.trim().toUpperCase()) ?? null;
  const needle = text.toLowerCase();
  const matches = (value: string | undefined): boolean => value !== undefined && value.toLowerCase().includes(needle);
  const key = REFERENCE_WANTED[searchIn];
  const found = index.entries.filter((target) =>
    key !== undefined
      ? matches(target.strings.get(key))
      : searchIn === 'O' && [...target.strings].some(([name, value]) => !REFERENCE_NATIVE_KEYS.has(name) && matches(value)),
  );
  return found.length === 1 ? found[0]! : null;
}

export interface ResolvedStrings {
  readonly strings: Map<string, string>;
  /** Entrees designees directement par une reference resolue. */
  readonly targets: ReadonlySet<string>;
  /** References laissees telles quelles : cible absente ou ambigue, champ voulu inconnu, boucle. */
  readonly unresolved: number;
}

/** Chaines de l'entree, references remplacees par la valeur designee (resolue a son tour, sans boucle). */
export function resolveEntryReferences(strings: ReadonlyMap<string, string>, uuid: string, index: ReferenceIndex): ResolvedStrings {
  const targets = new Set<string>();
  let unresolved = 0;
  const resolve = (text: string, trail: readonly string[]): string =>
    text.replace(REFERENCE_PATTERN, (whole, wanted: string, searchIn: string, search: string) => {
      const key = REFERENCE_WANTED[wanted.toUpperCase()];
      const target = key === undefined ? null : findReferenceTarget(index, searchIn.toUpperCase(), search);
      const step = target === null ? '' : `${target.uuid}/${key}`;
      if (target === null || trail.includes(step) || trail.length > MAX_REFERENCE_DEPTH) {
        unresolved += 1;
        return whole;
      }
      // Une reference en echec plus loin dans la chaine fait echouer celle-ci : jamais le texte d'une autre entree.
      const before = unresolved;
      const value = resolve(target.strings.get(key!) ?? '', [...trail, step]);
      if (unresolved > before) {
        unresolved = before + 1;
        return whole;
      }
      if (trail.length === 1) targets.add(target.uuid);
      return value;
    });
  const resolved = new Map<string, string>();
  for (const [key, value] of strings) resolved.set(key, resolve(value, [`${uuid}/${key}`]));
  return { strings: resolved, targets, unresolved };
}
